/* eslint-disable jsx-a11y/anchor-has-content */
import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import {
  useEditorRef,
  useFloatingLinkSelectors,
  floatingLinkSelectors,
  mergeProps,
  useComposedRef,
  unwrapLink,
  getLinkAttributes,
  findNode,
  getPluginType,
  triggerFloatingLinkEdit,
  floatingLinkActions,
  focusEditor,
  ELEMENT_LINK,
  usePlateSelection,
} from '@udecode/plate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import { useFloatingLinkEdit } from './useFloatingLinkEdit';
import { useFloatingLinkInsert } from './useFloatingLinkInsert';

const protocolRE = /^(?:\w+:)?\/\//;

export const useFloatingLinkUrlInput = (props) => {
  const updated = useFloatingLinkSelectors().updated();
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && updated) {
      setTimeout(() => {
        ref.current?.focus();
      }, 0);
    }
  }, [updated]);

  const onChange = useCallback((e) => {
    const { value } = e.target;
    const match = value.match(protocolRE);

    if (!match) {
      if (value.length === 1) {
        e.target.value = `https://${value}`;
      }
      else {
        e.target.value = 'https://';
      }
    }
    floatingLinkActions.url(e.target.value);
  }, []);

  return mergeProps(
    {
      onChange,
      defaultValue: floatingLinkSelectors.url(),
    },
    { ...props, ref: useComposedRef(props.ref, ref) },
  );
};

export const FloatingLinkUrlInput = (props) => {
  const htmlProps = useFloatingLinkUrlInput(props);

  return (
    <div className="slate-ui-floating__input">
      <FontAwesomeIcon icon={['fal', 'external-link']}
        size="lg"
      />
      <input {...htmlProps} />
    </div>
  );
};

export const useFloatingLinkTextInput = (props) => {
  const onChange = useCallback((e) => {
    floatingLinkActions.text(e.target.value);
  }, []);

  return mergeProps(
    {
      onChange,
      defaultValue: floatingLinkSelectors.text(),
    },
    props,
  );
};

export const FloatingLinkTextInput = (props) => {
  const htmlProps = useFloatingLinkTextInput(props);

  return (
    <div className="slate-ui-floating__input">
      <FontAwesomeIcon icon={['fal', 'align-left']}
        size="lg"
      />
      <input {...htmlProps} />
    </div>
  );
};

export const PlateFloatingLink = ({ readOnly }) => {
  const store = useFloatingLinkSelectors();
  const isEditing = store.isEditing();

  if (readOnly) return null;

  const input = (
    <div className="slate-ui-floating__inner form-group">
      <FloatingLinkUrlInput
        placeholder="Paste link"
      />
      <div className="slate-ui-floating__divider" />
      <FloatingLinkTextInput
        placeholder="Text to display"
      />
    </div>
  );

  const editContent = !isEditing ? (
    <div className="slate-ui-floating__inner slate-ui-floating__inner--inline">
      <FloatingLinkEditButton>
        Edit link
      </FloatingLinkEditButton>

      <div className="slate-ui-floating__divider" />

      <OpenLinkButton>
        <FontAwesomeIcon icon={['fa', 'external-link']} />
      </OpenLinkButton>

      <div className="slate-ui-floating__divider" />

      <UnlinkButton>
        Unlink
      </UnlinkButton>
    </div>
  ) : (
    input
  );

  return (
    <>
      <FloatingLinkInsertRoot>
        {input}
      </FloatingLinkInsertRoot>

      <FloatingLinkEditRoot>
        {editContent}
      </FloatingLinkEditRoot>
    </>
  );
};

export const useFloatingLinkEditButton = (props) => {
  const editor = useEditorRef();

  return {
    onClick: useCallback(() => {
      triggerFloatingLinkEdit(editor);
    }, [editor]),
    ...props,
  };
};

export const FloatingLinkEditButton = (props) => {
  const { className, ...rest } = props;
  const htmlProps = useFloatingLinkEditButton(rest);
  htmlProps.className = clsx(
    className,
    'slate-ui-floating__button',
    'button--unstyled',
  );

  return (
    <button {...htmlProps} />
  );
};

export const useOpenLinkButton = (props) => {
  const editor = useEditorRef();
  const selection = usePlateSelection();

  const entry = useMemo(
    () =>
      findNode(editor, {
        match: { type: getPluginType(editor, ELEMENT_LINK) },
      }),
    [editor, selection],
  );

  if (!entry) {
    return {};
  }

  const [element] = entry;
  const linkAttributes = getLinkAttributes(editor, element);

  return {
    ...linkAttributes,
    target: '_blank',
    'aria-label': 'Open link in a new tab',
    onMouseOver: (e) => {
      e.stopPropagation();
    },
    ...props,
  };
};

export const OpenLinkButton = (props) => {
  const { className, ...rest } = props;
  const htmlProps = useOpenLinkButton(rest);
  htmlProps.className = clsx(
    className,
    'slate-ui-floating__button',
    'slate-ui-floating__button--link',
  );

  return (
    <a {...htmlProps} />
  );
};

export const FloatingLinkInsertRoot = (props) => {
  const { className, ...rest } = props;
  const htmlProps = useFloatingLinkInsert(rest);
  htmlProps.className = clsx(
    className,
    'slate-ui-floating',
    'slate-ui-floating--link',
    'slate-ui-floating--insert',
  );

  if (htmlProps.style?.display === 'none') {
    return null;
  }

  return (
    <div {...htmlProps} />
  );
};

export const FloatingLinkEditRoot = (props) => {
  const { className, ...rest } = props;
  const htmlProps = useFloatingLinkEdit(rest);
  htmlProps.className = clsx(
    className,
    'slate-ui-floating',
    'slate-ui-floating--link',
    'slate-ui-floating--edit',
    'slate-ui-floating--width-auto',
  );

  if (htmlProps.style?.display === 'none') {
    return null;
  }

  return (
    <div {...htmlProps} />
  );
};

export const useUnlinkButton = (props) => {
  const editor = useEditorRef();

  return {
    onClick: useCallback(() => {
      unwrapLink(editor);
      focusEditor(editor, editor.selection);
    }, [editor]),
    ...props,
  };
};

export const UnlinkButton = (props) => {
  const { className, ...rest } = props;
  const htmlProps = useUnlinkButton(rest);
  htmlProps.className = clsx(
    className,
    'slate-ui-floating__button',
    'button--unstyled',
  );

  return (
    <button {...htmlProps} />
  );
};
